<template>
  <!-- 側邊欄容器：fixed 全高 -->
  <aside
    class="fixed left-0 top-0 h-screen flex flex-col z-40 transition-[width] duration-200 ease-in-out border-r border-surface-light/30 bg-bg"
    :style="{ width: collapsed ? '72px' : '260px' }"
  >
    <!-- ── 頂部：logo + 折疊按鈕 ─────────────────────────────── -->
    <div class="relative flex items-center px-4 pt-5 pb-3 shrink-0">
      <!-- Logo -->
      <div class="flex items-center gap-2 overflow-hidden min-w-0">
        <!-- 折疊時：M 徽章 -->
        <div
          class="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-secondary to-primary flex items-center justify-center font-extrabold text-lg text-fg select-none"
        >
          M
        </div>
        <!-- 展開時：MINICAT 字樣 -->
        <span
          v-if="!collapsed"
          class="bg-gradient-to-r from-secondary to-primary-light bg-clip-text text-transparent font-extrabold tracking-wide text-lg whitespace-nowrap"
        >
          MINICAT
        </span>
      </div>

      <!-- 折疊切換按鈕 -->
      <button
        class="absolute -right-3 top-6 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-fg hover:bg-primary-light transition-colors shadow-lg"
        :title="collapsed ? '展開' : '收起'"
        @click="toggleCollapse"
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="w-3 h-3 transition-transform duration-200"
          :class="collapsed ? 'rotate-180' : ''"
        >
          <path d="M13 4l-6 6 6 6" />
        </svg>
      </button>
    </div>

    <!-- ── 使用者區塊 ──────────────────────────────────────────── -->
    <div class="px-3 pb-3 shrink-0">
      <div
        class="rounded-xl p-3"
        :class="collapsed ? 'flex justify-center' : 'bg-surface/60'"
      >
        <!-- 頭像 -->
        <div
          class="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center font-bold text-fg text-sm select-none"
        >
          {{ accountInitial }}
        </div>

        <!-- 展開時顯示帳號 + 餘額 -->
        <template v-if="!collapsed">
          <div class="mt-2">
            <p class="text-sm font-medium text-fg truncate">
              {{ auth.playerAccount || '—' }}
            </p>
            <div class="flex items-center gap-1.5 mt-1">
              <!-- 錢包圖示 -->
              <svg
                class="w-3.5 h-3.5 text-accent shrink-0"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  x="2"
                  y="5"
                  width="16"
                  height="12"
                  rx="2"
                />
                <path d="M14 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                <path d="M2 9h16" />
              </svg>
              <span class="text-sm font-medium text-accent">{{ balance ?? '...' }}</span>
              <!-- 刷新按鈕 -->
              <button
                :disabled="auth.walletRefreshing"
                :title="t('refreshBalance')"
                class="ml-auto w-5 h-5 flex items-center justify-center rounded-full text-fg-subtle hover:text-accent hover:bg-surface-light/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                @click="auth.refreshWalletBalance()"
              >
                <svg
                  class="w-3.5 h-3.5"
                  :class="{ 'animate-spin': auth.walletRefreshing }"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 10a7 7 0 0 1 12-4.95L17 7" />
                  <path d="M17 3v4h-4" />
                  <path d="M17 10a7 7 0 0 1-12 4.95L3 13" />
                  <path d="M3 17v-4h4" />
                </svg>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 分隔線 -->
    <div class="mx-3 border-t border-surface-light/20 mb-2 shrink-0" />

    <!-- ── 導覽選單 ────────────────────────────────────────────── -->
    <nav class="flex-1 px-2 space-y-1 overflow-y-auto overflow-x-hidden">
      <!-- 大廳/Games -->
      <router-link
        to="/"
        class="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors group"
        :class="isGamesActive
          ? 'bg-primary/20 text-primary-light'
          : 'text-fg-muted hover:bg-surface hover:text-fg'"
        :title="collapsed ? t('games') : undefined"
      >
        <!-- Home icon -->
        <svg
          class="w-5 h-5 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 12L12 3l9 9" />
          <path d="M9 21V12h6v9" />
          <path d="M3 12v9h18v-9" />
        </svg>
        <span
          v-if="!collapsed"
          class="text-sm truncate"
          :class="isGamesActive ? 'font-medium' : ''"
        >{{ t('games') }}</span>
      </router-link>

      <!-- Wallets（seamless 模式隱藏） -->
      <router-link
        v-if="!auth.isSeamless"
        to="/wallets"
        class="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors"
        :class="route.name === 'wallets'
          ? 'bg-primary/20 text-primary-light'
          : 'text-fg-muted hover:bg-surface hover:text-fg'"
        :title="collapsed ? t('wallets') : undefined"
      >
        <!-- Wallet icon -->
        <svg
          class="w-5 h-5 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="2"
            y="6"
            width="20"
            height="14"
            rx="2"
          />
          <path d="M16 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
          <path d="M2 11h20" />
        </svg>
        <span
          v-if="!collapsed"
          class="text-sm truncate"
          :class="route.name === 'wallets' ? 'font-medium' : ''"
        >{{ t('wallets') }}</span>
      </router-link>

      <!-- Bet Logs -->
      <router-link
        to="/bet-logs"
        class="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors"
        :class="route.name === 'bet-logs'
          ? 'bg-primary/20 text-primary-light'
          : 'text-fg-muted hover:bg-surface hover:text-fg'"
        :title="collapsed ? t('betLogs') : undefined"
      >
        <!-- Dice/List icon -->
        <svg
          class="w-5 h-5 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="2"
            y="2"
            width="20"
            height="20"
            rx="3"
          />
          <circle
            cx="8"
            cy="8"
            r="1.5"
            fill="currentColor"
          />
          <circle
            cx="16"
            cy="8"
            r="1.5"
            fill="currentColor"
          />
          <circle
            cx="8"
            cy="16"
            r="1.5"
            fill="currentColor"
          />
          <circle
            cx="16"
            cy="16"
            r="1.5"
            fill="currentColor"
          />
          <circle
            cx="12"
            cy="12"
            r="1.5"
            fill="currentColor"
          />
        </svg>
        <span
          v-if="!collapsed"
          class="text-sm truncate"
          :class="route.name === 'bet-logs' ? 'font-medium' : ''"
        >{{ t('betLogs') }}</span>
      </router-link>

      <!-- Transactions -->
      <router-link
        to="/transactions"
        class="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors"
        :class="route.name === 'transactions'
          ? 'bg-primary/20 text-primary-light'
          : 'text-fg-muted hover:bg-surface hover:text-fg'"
        :title="collapsed ? t('transactions') : undefined"
      >
        <!-- Swap icon -->
        <svg
          class="w-5 h-5 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M7 16V4m0 0L3 8m4-4l4 4" />
          <path d="M17 8v12m0 0l4-4m-4 4l-4-4" />
        </svg>
        <span
          v-if="!collapsed"
          class="text-sm truncate"
          :class="route.name === 'transactions' ? 'font-medium' : ''"
        >{{ t('transactions') }}</span>
      </router-link>
    </nav>

    <!-- ── 底部：語言 + 登出 ───────────────────────────────────── -->
    <div class="px-2 pb-4 pt-2 space-y-2 shrink-0 border-t border-surface-light/20">
      <!-- 語言選擇（展開時顯示 select，折疊時隱藏） -->
      <div
        v-if="!collapsed"
        class="px-1"
      >
        <select
          :value="auth.locale"
          class="w-full text-xs border border-surface-light/30 rounded-lg px-3 py-2 bg-surface text-fg-muted focus:outline-none focus:ring-1 focus:ring-primary/50 cursor-pointer"
          @change="auth.setLocale($event.target.value)"
        >
          <option value="en">
            EN
          </option>
          <option value="zh-CN">
            中文
          </option>
        </select>
      </div>

      <!-- 登出按鈕 -->
      <button
        class="w-full flex items-center justify-center gap-2 rounded-lg py-2 px-3 font-medium text-sm text-fg transition-opacity hover:opacity-90 bg-gradient-to-r from-secondary to-primary"
        :title="collapsed ? t('logout') : undefined"
        @click="handleLogout"
      >
        <!-- Logout icon -->
        <svg
          class="w-5 h-5 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line
            x1="21"
            y1="12"
            x2="9"
            y2="12"
          />
        </svg>
        <span v-if="!collapsed">{{ t('logout') }}</span>
      </button>
    </div>
  </aside>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '~/player/stores/auth'
import { useI18n } from '~/player/i18n'

// ── Props / Emits ─────────────────────────────────────────────────
const props = defineProps({
  collapsed: {
    type: Boolean,
    required: true,
  },
})
const emit = defineEmits(['update:collapsed'])

// ── 依賴 ─────────────────────────────────────────────────────────
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n(auth)

// ── 計算 ─────────────────────────────────────────────────────────
const balance = computed(() => auth.walletBalance)

const accountInitial = computed(() => {
  const acc = auth.playerAccount
  return acc ? acc[0].toUpperCase() : '?'
})

const isGamesActive = computed(() =>
  ['providers', 'categories', 'games'].includes(route.name)
)

// ── 折疊切換 ─────────────────────────────────────────────────────
function toggleCollapse() {
  const next = !props.collapsed
  emit('update:collapsed', next)
  try {
    localStorage.setItem('player_sidebar_collapsed', next ? '1' : '0')
  } catch {
    // 忽略
  }
}

// ── 生命週期 ──────────────────────────────────────────────────────
onMounted(() => {
  auth.fetchWalletBalance()
  auth.ensureProfile()
})

// ── 登出 ─────────────────────────────────────────────────────────
async function handleLogout() {
  await auth.logout()
  router.push('/login')
}
</script>
